import { ObjectId } from "@novorender/data-js-api";

import { binarySearchIncludes, buildBitset, dedupeSorted, mergeSorted } from "utils/arrays";

/**
 * Read-only list of object ids: either a plain array or a typed array (such as
 * a SelectionSet's backing store). Use for parameters that only iterate/read.
 */
export type IdList = readonly ObjectId[] | Uint32Array;

/**
 * Immutable set of object ids for the highlighted/hidden selection contexts.
 *
 * Replaces the previous `Record<ObjectId, true>` + `ObjectId[]` pair, which
 * cost ~23 bytes per id (measured: 28 MB for a 1.25M-id select-all) and was
 * fully copied on every selection change. This stores the ids once, as a
 * sorted deduplicated `Uint32Array` (4 bytes per id), and set operations
 * build the next array in a single pass instead of spreading a Record.
 *
 * Membership checks use binary search for small sets and a lazily built
 * bitset (1 bit per id in [0..maxId]) for large ones, where render-path
 * loops check hundreds of thousands of ids per pass.
 */
export class SelectionSet {
    /** Sorted, deduplicated. Never mutated after construction. */
    private readonly data: Uint32Array;
    /** Lazy: built on first `has` when the set is large. 1 bit per id in [0..maxId]. */
    private bitset: Uint8Array | undefined;

    /** Sets below this size answer `has` by binary search instead of a bitset. */
    private static readonly BITSET_MIN_SIZE = 512;

    static readonly EMPTY = new SelectionSet(new Uint32Array(0));

    /** @param sorted MUST be sorted and deduplicated. */
    private constructor(sorted: Uint32Array) {
        this.data = sorted;
    }

    static fromIds(ids: Iterable<ObjectId>): SelectionSet {
        if (ids instanceof SelectionSet) {
            return ids;
        }
        const sorted = new Uint32Array(ids as Iterable<number>).sort();
        const deduped = dedupeSorted(sorted);
        return SelectionSet.fromSortedIds(deduped);
    }

    /**
     * Wrap an already-sorted, already-deduplicated buffer without copying.
     * Used when expanding a shrunk snapshot (`IdArrayShrinker.expand`).
     * `sorted` MUST NOT be mutated afterwards.
     */
    static fromSortedIds(sorted: Uint32Array): SelectionSet {
        return sorted.length === 0 ? SelectionSet.EMPTY : new SelectionSet(sorted);
    }

    get size(): number {
        return this.data.length;
    }

    /**
     * The backing sorted array. Exposed for iteration/handoff (e.g. engine
     * highlight sets, which want sorted ids); MUST NOT be mutated.
     */
    get sortedIds(): Uint32Array {
        return this.data;
    }

    /** Highest id, or 0 when empty. The lazy bitset is `(maxId >>> 3) + 1` bytes. */
    get maxId(): number {
        return this.data.length === 0 ? 0 : this.data[this.data.length - 1];
    }

    /** 0 until `has()` has built the bitset (large sets only). */
    get bitsetBytes(): number {
        return this.bitset?.byteLength ?? 0;
    }

    /**
     * Size the bitset would be / is, for sets that use one.
     * 0 for empty and small sets (binary-search `has`, no bitset).
     */
    get bitsetCapacityBytes(): number {
        if (this.data.length < SelectionSet.BITSET_MIN_SIZE) {
            return 0;
        }
        return (this.maxId >>> 3) + 1;
    }

    has(id: ObjectId): boolean {
        const data = this.data;
        if (data.length === 0 || id < data[0] || id > data[data.length - 1]) {
            return false;
        }

        if (data.length >= SelectionSet.BITSET_MIN_SIZE) {
            if (!this.bitset) {
                this.bitset = buildBitset(data);
            }
            return (this.bitset[id >>> 3] & (1 << (id & 7))) !== 0;
        }

        return binarySearchIncludes(data, id);
    }

    withAdded(ids: Iterable<ObjectId>): SelectionSet {
        const additions = new Uint32Array(ids as Iterable<number>).sort();
        if (additions.length === 0) {
            return this;
        }
        if (this.data.length === 0) {
            return new SelectionSet(dedupeSorted(additions));
        }
        return new SelectionSet(mergeSorted(this.data, additions));
    }

    /**
     * Add a single id, returning `this` unchanged if it's already a member.
     * Binary-searches the insertion point instead of `withAdded`'s
     * array-wrap + sort + linear merge, which matters for the common
     * single-id case (e.g. folding a "main object" into a selection).
     */
    withSingleAdded(id: ObjectId): SelectionSet {
        const data = this.data;
        if (data.length === 0) {
            return new SelectionSet(new Uint32Array([id]));
        }

        let low = 0;
        let high = data.length;
        while (low < high) {
            const mid = (low + high) >>> 1;
            if (data[mid] < id) {
                low = mid + 1;
            } else {
                high = mid;
            }
        }

        if (data[low] === id) {
            return this;
        }

        const result = new Uint32Array(data.length + 1);
        result.set(data.subarray(0, low));
        result[low] = id;
        result.set(data.subarray(low), low + 1);
        return new SelectionSet(result);
    }

    withRemoved(ids: Iterable<ObjectId>): SelectionSet {
        if (ids instanceof SelectionSet) {
            return this.difference(ids);
        }
        return this.withRemovedAndAdded(ids, undefined);
    }

    /**
     * Ids in this set that are not in `other`. Same idea as {@link Set.difference}:
     * walk this set and test membership in the other. Returns `this` / `EMPTY`
     * when the result is identical to one of those, and otherwise builds a new
     * set from the already-sorted remainder via the private constructor (no re-sort).
     */
    difference(other: SelectionSet): SelectionSet {
        if (this.size === 0 || this === other) {
            return SelectionSet.EMPTY;
        }
        if (other.size === 0) {
            return this;
        }

        const kept = new Uint32Array(this.size);
        let count = 0;
        for (const id of this) {
            if (!other.has(id)) {
                kept[count++] = id;
            }
        }

        if (count === 0) {
            return SelectionSet.EMPTY;
        }
        if (count === this.size) {
            return this;
        }
        // subarray shares the oversized buffer; slice to release the tail.
        return new SelectionSet(kept.subarray(0, count).slice());
    }

    /**
     * Ids present in both sets. Same idea as {@link Set.intersection}: walk the
     * smaller set and test membership in the larger (`has` is a bitset lookup
     * once the set is large). Returns `this` / `other` / `EMPTY` when the
     * result is identical to one of those, and otherwise builds a new set from
     * the already-sorted overlap via the private constructor (no re-sort).
     */
    intersection(other: SelectionSet): SelectionSet {
        if (this.size === 0 || other.size === 0) {
            return SelectionSet.EMPTY;
        }

        if (this === other) {
            return this;
        }

        const smaller = this.size <= other.size ? this : other;
        const larger = smaller === this ? other : this;
        const kept = new Uint32Array(smaller.size);
        let count = 0;
        for (const id of smaller) {
            if (larger.has(id)) {
                kept[count++] = id;
            }
        }

        if (count === 0) {
            return SelectionSet.EMPTY;
        }

        if (count === this.size) {
            return this;
        }

        if (count === other.size) {
            return other;
        }

        // subarray shares the oversized buffer; slice to release the tail.
        return new SelectionSet(kept.subarray(0, count).slice());
    }

    /**
     * Remove and add in one pass (single output allocation), for the
     * remove-and-add selection action.
     */
    withRemovedAndAdded(remove: Iterable<ObjectId>, add: Iterable<ObjectId> | undefined): SelectionSet {
        const removeSet = new Set(remove);
        let kept: Uint32Array;
        if (removeSet.size === 0) {
            kept = this.data;
        } else {
            kept = new Uint32Array(this.data.length);
            let count = 0;
            for (const id of this.data) {
                if (!removeSet.has(id)) {
                    kept[count++] = id;
                }
            }
            if (count === kept.length) {
                // Nothing was actually removed — reuse the original backing array so
                // the no-additions path below returns `this` (bitset and reference
                // equality preserved).
                kept = this.data;
            } else {
                kept = kept.subarray(0, count);
            }
        }

        const additions = add === undefined ? new Uint32Array(0) : new Uint32Array(add as Iterable<number>).sort();
        if (additions.length === 0) {
            if (kept === this.data) {
                return this;
            }
            // subarray shares the oversized buffer; slice to release the tail.
            return new SelectionSet(kept.slice());
        }
        if (kept.length === 0) {
            return new SelectionSet(dedupeSorted(additions));
        }
        return new SelectionSet(mergeSorted(kept, additions));
    }

    [Symbol.iterator](): IterableIterator<ObjectId> {
        return this.data[Symbol.iterator]() as IterableIterator<ObjectId>;
    }

    /** Materialize a plain array (serialization boundaries: bookmarks, viewpoints, ...). */
    toArray(): ObjectId[] {
        return Array.from(this.data);
    }
}
